import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CONTRACT_PLACEHOLDERS, type ContractTemplateView, type StaffListItem, unknownPlaceholders } from "@vaulted/shared";
import { useRef, useState } from "react";
import { errorMessage, get, post, put } from "../api";
import { ContractText, MyContracts } from "../components/StaffContracts";
import { Alert, Button, Field, PageHeader, Panel, PanelHeader, SelectField, TextAreaField } from "../components/ui";

/** Settings → Staff contracts: the wording, with {{placeholders}} filled in for each person when it's sent. */
export function StaffContractsSettingsPage() {
  const q = useQuery({ queryKey: ["contract-templates"], queryFn: () => get<{ templates: ContractTemplateView[]; signers: { id: string; name: string }[] }>("/api/staff-contracts/templates") });
  const [editing, setEditing] = useState<ContractTemplateView | "new" | null>(null);
  const list = q.data?.templates ?? [];
  return (
    <div className="max-w-4xl">
      <PageHeader
        title="Staff contracts"
        description="Paste in the contract's wording. Placeholders like {{staff.name}} are filled in for each person when you send it (Staff → name → Contract); they sign it, then the clinic signer does, and a PDF of both is saved."
        action={
          !editing && (
            <Button variant="primary" onClick={() => setEditing("new")}>
              New contract
            </Button>
          )
        }
      />
      <div className="flex flex-col gap-6">
        <MyContracts />
        {editing ? (
          <Editor t={editing === "new" ? null : editing} onClose={() => setEditing(null)} />
        ) : (
          <Panel>
            <PanelHeader title="Contracts" />
            <ul className="divide-y divide-line">
              {!list.length && <li className="px-5 py-5 text-sm text-muted">None yet. Add one, give it a name, and paste the wording in when you have it.</li>}
              {list.map((t) => (
                <li key={t.id} className="flex flex-wrap items-center gap-3 px-5 py-3">
                  <span className={`font-semibold ${t.archived ? "text-muted line-through" : ""}`}>{t.name}</span>
                  <span className="text-sm text-muted">
                    version {t.version}
                    {!t.body.trim() ? " · no wording yet" : ""}
                    {t.unknown.length ? ` · ${t.unknown.length} placeholder${t.unknown.length === 1 ? "" : "s"} to fix` : ""}
                  </span>
                  <Button variant="quiet" className="ml-auto" onClick={() => setEditing(t)}>
                    Edit
                  </Button>
                </li>
              ))}
            </ul>
          </Panel>
        )}
      </div>
    </div>
  );
}

function Editor({ t, onClose }: { t: ContractTemplateView | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState(t?.name ?? "Independent contractor agreement");
  const [body, setBody] = useState(t?.body ?? "");
  const [archived, setArchived] = useState(t?.archived ?? false);
  const area = useRef<HTMLTextAreaElement | null>(null);
  const staff = useQuery({ queryKey: ["staff"], queryFn: () => get<StaffListItem[]>("/api/staff"), retry: false });
  const [previewFor, setPreviewFor] = useState("");
  const preview = useMutation({ mutationFn: () => post<{ text: string; missing: string[]; unknown: string[] }>("/api/staff-contracts/preview", { body, staffMemberId: previewFor }) });
  const save = useMutation({
    mutationFn: () => (t ? put(`/api/staff-contracts/templates/${t.id}`, { name, body, archived }) : post("/api/staff-contracts/templates", { name, body })),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["contract-templates"] });
      onClose();
    },
  });
  const bad = unknownPlaceholders(body);
  // Clicking a placeholder puts it where the cursor is.
  const insert = (key: string) => {
    const el = area.current;
    const token = `{{${key}}}`;
    if (!el) return setBody((b) => b + token);
    const at = el.selectionStart ?? body.length;
    const next = body.slice(0, at) + token + body.slice(el.selectionEnd ?? at);
    setBody(next);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(at + token.length, at + token.length);
    });
  };
  return (
    <Panel>
      <PanelHeader title={t ? `Edit: ${t.name}` : "New contract"} description={t ? `Version ${t.version}. Changing the wording makes version ${t.version + 1}; contracts already sent keep the wording they were sent with.` : undefined} />
      <div className="flex flex-col gap-4 px-5 py-5">
        {save.error != null && <Alert>{errorMessage(save.error)}</Alert>}
        <Field label="Name (the contract's title)" maxLength={120} value={name} onChange={(e) => setName(e.target.value)} />
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_240px]">
          <div ref={(el) => void (area.current = el?.querySelector("textarea") ?? null)}>
            <TextAreaField
              label="Wording"
              rows={22}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder={"# 1. Services\n{{staff.name}} will provide nursing services for {{clinic.name}} starting {{start_date}}.\n\n# 2. Pay\n- Commission: {{pay.commission}} of what clients pay\n- Tips: {{pay.tips}}"}
              hint="Paste from Word or Google Docs. A blank line starts a new paragraph, a line starting with # is a heading, and a line starting with - is a bullet."
            />
          </div>
          <div>
            <div className="mb-1 text-sm font-semibold text-ink-soft">Placeholders (click to insert)</div>
            <ul className="flex flex-col gap-1 text-sm">
              {CONTRACT_PLACEHOLDERS.map((p) => (
                <li key={p.key}>
                  <button type="button" className="w-full rounded-lg px-2 py-1 text-left hover:bg-mist" onClick={() => insert(p.key)} title={`e.g. ${p.example}`}>
                    <code className="text-brand">{`{{${p.key}}}`}</code>
                    <span className="block text-xs text-muted">{p.label}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
        {bad.length > 0 && <Alert>These aren't placeholders (check the spelling): {bad.map((b) => `{{${b}}}`).join(", ")}. A contract can't be sent until they're fixed.</Alert>}
        <div className="flex flex-wrap items-end gap-3 rounded-xl border border-line p-3">
          <SelectField label="Preview as" value={previewFor} onChange={(e) => setPreviewFor(e.target.value)} className="w-64">
            <option value="">Pick someone…</option>
            {(staff.data ?? [])
              .filter((s) => s.status !== "deactivated")
              .map((s) => (
                <option key={s.id} value={s.id}>
                  {s.firstName} {s.lastName}
                </option>
              ))}
          </SelectField>
          <Button variant="quiet" disabled={!previewFor || !body.trim()} busy={preview.isPending} onClick={() => preview.mutate()}>
            Preview
          </Button>
        </div>
        {preview.data && (
          <div className="flex flex-col gap-2">
            {preview.data.missing.length > 0 && <Alert tone="info">No value for them yet: {preview.data.missing.map((m) => `{{${m}}}`).join(", ")} (shown as ________).</Alert>}
            <div className="max-h-[50vh] overflow-y-auto rounded-xl border border-line p-4">
              <h2 className="mb-2 text-lg font-bold">{name}</h2>
              <ContractText text={preview.data.text} />
            </div>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-3">
          <Button variant="primary" busy={save.isPending} disabled={!name.trim()} onClick={() => save.mutate()}>
            Save
          </Button>
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          {t && (
            <label className="ml-auto flex items-center gap-2 text-sm">
              <input type="checkbox" checked={archived} onChange={(e) => setArchived(e.target.checked)} /> Retired (can't be sent; signed ones are kept)
            </label>
          )}
        </div>
      </div>
    </Panel>
  );
}
